import { useState } from 'react';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { FeatureFlagName } from '@curbside-quest/shared';

/**
 * Debug panel for testing feature flags in development
 * Shows current flag states and allows manual refresh
 */
export function FeatureFlagDebugPanel() {
  const { isEnabled, isLoading, error, refresh } = useFeatureFlag();
  const [isExpanded, setIsExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Only show in development
  if (!import.meta.env.DEV) {
    return null;
  }

  const flags: FeatureFlagName[] = [
    'AI_ANALYSIS_ENABLED',
    'AI_SIMULATE_FAILURE',
    'AI_SERVICE_UNAVAILABLE',
    'ADVANCED_SEARCH_ENABLED',
    'PREMIUM_FEATURES_ENABLED',
    'DEBUG_MODE_ENABLED',
    'MAINTENANCE_MODE',
    'CONSOLE_LOGGING_ENABLED'
  ];

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-cardboard hover:bg-cardboard-600 text-white p-3 rounded-full shadow-lg transition-all duration-200 hover:shadow-xl"
        title="Feature Flag Debug Panel"
      >
        <span className="text-lg">🚩</span>
      </button>

      {/* Debug Panel */}
      {isExpanded && (
        <div className="absolute bottom-16 right-0 w-80 bg-white rounded-lg shadow-xl border border-urban/10 p-4 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-urban">Feature Flags</h3>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="bg-urban/10 hover:bg-urban/20 text-urban px-3 py-1 rounded text-sm transition-all duration-200 disabled:opacity-50"
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-4">
              <div className="animate-spin w-6 h-6 border-2 border-cardboard border-t-transparent rounded-full mx-auto"></div>
              <p className="text-sm text-urban/70 mt-2">Loading flags...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-rust/10 border border-rust/20 rounded-lg p-3 mb-4">
              <p className="text-sm text-rust font-medium">Error loading flags:</p>
              <p className="text-xs text-rust/70 mt-1">{error.message}</p>
            </div>
          )}

          {/* Flag List */}
          <div className="space-y-2">
            {flags.map((flag) => {
              const enabled = isEnabled(flag);
              const envOverride = getEnvironmentOverride(flag);
              
              return (
                <div
                  key={flag}
                  className={`flex items-center justify-between p-2 rounded-lg border ${
                    enabled 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium text-urban">
                      {flag.replace(/_/g, ' ').toLowerCase()}
                    </div>
                    {envOverride !== undefined && (
                      <div className="text-xs text-urban/60">
                        ENV Override: {envOverride.toString()}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span
                      className={`w-3 h-3 rounded-full ${
                        enabled ? 'bg-green-500' : 'bg-red-500'
                      }`}
                    />
                    <span className="text-xs font-mono text-urban/70">
                      {enabled ? 'ON' : 'OFF'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Environment Info */}
          <div className="mt-4 pt-4 border-t border-urban/10">
            <div className="text-xs text-urban/60 space-y-1">
              <div>Environment: {import.meta.env.MODE}</div>
              <div>Dev Mode: {import.meta.env.DEV ? 'Yes' : 'No'}</div>
              <div>Local Development: {isLocalDevelopment() ? 'Yes (Env vars only)' : 'No (Remote Config)'}</div>
              <div>Project ID: {import.meta.env.VITE_FIREBASE_PROJECT_ID || 'demo-curbside-quest'}</div>
              <div>Loading: {isLoading ? 'Yes' : 'No'}</div>
              <div>Error: {error ? 'Yes' : 'No'}</div>
              <div>Last Updated: {new Date().toLocaleTimeString()}</div>
            </div>
          </div>

          {/* Remote Config Debug Info */}
          {!isLocalDevelopment() && (
            <div className="mt-4 pt-4 border-t border-urban/10">
              <div className="text-xs text-urban/60 space-y-1">
                <div className="font-medium text-urban">Remote Config Status:</div>
                <div>Fetch Interval: {import.meta.env.MODE === 'staging' ? '60s' : '3600s'}</div>
                <div>Last Fetch: {new Date().toLocaleTimeString()}</div>
                <button
                  onClick={() => {
                    console.log('🔧 Manual refresh triggered from debug panel');
                    handleRefresh();
                  }}
                  className="mt-2 bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
                >
                  Force Refresh Remote Config
                </button>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="mt-4 pt-4 border-t border-urban/10">
            <p className="text-xs text-urban/60">
              {isLocalDevelopment() 
                ? '💡 Local dev: Edit .env.local to change feature flags (e.g., VITE_FEATURE_AI_ANALYSIS_ENABLED=true)'
                : '💡 Production: Environment variables override Remote Config values'
              }
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Check if we're in local development with demo project
 */
function isLocalDevelopment(): boolean {
  return import.meta.env.DEV && (
    !import.meta.env.VITE_FIREBASE_PROJECT_ID || 
    import.meta.env.VITE_FIREBASE_PROJECT_ID.startsWith('demo-')
  );
}

/**
 * Get environment variable override for a flag
 */
function getEnvironmentOverride(flagName: FeatureFlagName): boolean | undefined {
  const envVarName = `VITE_FEATURE_${flagName}`;
  const envValue = import.meta.env[envVarName];
  
  if (envValue !== undefined) {
    return envValue.toLowerCase() === 'true';
  }
  
  return undefined;
}